import { useClientPortalAPI, useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  Container,
  Layout,
  PrimaryButton,
  SecondaryTopNav,
  Text,
} from "@certn/cascadia-react";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { array, object, string } from "yup";

import { CreateApplyLinkForm } from "./components/CreateApplyLinkForm";
import { BillingDetailsModal } from "../../components/package-card/BillingDetailsModal";
import { useApplyLink } from "../../hooks/useApplyLink";
import { LAST_SELECTED_CLIENT_GROUP_KEY } from "../../utils/check-utils";

export function CreateApplyLink() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [validateJsonForm, setValidateJsonForm] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(
    localStorage.getItem(LAST_SELECTED_CLIENT_GROUP_KEY) || null
  );
  const {
    isBillingDetailsModalOpen,
    billingProfileId,
    hasValidBillingProfile,
    setIsBillingDetailsModalOpen,
  } = useApplyLink();

  const { data: groups, isLoading: isGroupsLoading } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/customer-groups/"
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/apply-links/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: (response: components["schemas"]["ApplyLink"]) => {
        navigate(`/apply-links?apply_link=${response.apply_link}`);
      },
    }
  );

  const allowedLanguages = ["en-CA", "fr-CA", "en-GB", "en-US", "en-AU"];

  const formSchema = object().shape({
    name: string().min(3).required().label(t("Apply link name")).trim(),
    customer_group: string().required().label(t("Group")),
    package: string().required().label(t("Package")),
    additional_message: string().label(t("Additional message")),
    // The client json form will validate its own data. This is just to block submitting the form.
    clientFormDataAndErrors: object()
      .shape({
        errors: array().length(0),
      })
      .required(),
    applicant_language: string()
      .oneOf(allowedLanguages, t("Invalid language"))
      .nullable(),
  });

  useEffect(() => {
    if (selectedGroup === null && groups?.length) {
      setSelectedGroup(groups[0].id);
    }
  }, [groups, selectedGroup]);

  return (
    <>
      <Formik
        initialValues={{
          name: "",
          customer_group: selectedGroup,
          package: "",
          additional_message: "",
          clientFormDataAndErrors: {
            data: {},
            errors: [],
          },
          applicant_language: null,
        }}
        onSubmit={(values) =>
          submit({
            ...values,
            client_form_data: values.clientFormDataAndErrors.data,
          })
        }
        validationSchema={formSchema}
      >
        {({
          handleSubmit,
          isSubmitting,
          isValid,
          values,
          setFieldValue,
          setTouched,
        }) => {
          // Other solutions, including initialValues, do not set the field value of customer_group, so this is needed.
          if (!values.customer_group && selectedGroup) {
            setFieldValue("customer_group", selectedGroup);
          }
          return (
            <Layout
              testID="create-apply-link-layout"
              header={
                <SecondaryTopNav
                  testID="close-button"
                  leftIcon="close"
                  onleftIconOnPress={() => navigate("/apply-links")}
                >
                  <PrimaryButton
                    onPress={() => {
                      if (!hasValidBillingProfile) {
                        setIsBillingDetailsModalOpen(true);
                        return;
                      }
                      setValidateJsonForm(true);
                      setTouched(
                        Object.keys(values).reduce((acc, key) => {
                          if (key === "clientFormDataAndErrors") return acc;
                          return {
                            ...acc,
                            [key]: true,
                          };
                        }, {})
                      );
                      if (isValid) handleSubmit();
                    }}
                    isLoading={isSubmitting}
                    testID="create-apply-link-button"
                  >
                    <Trans>Create link</Trans>
                  </PrimaryButton>
                </SecondaryTopNav>
              }
            >
              <Container layout="narrow">
                <Text
                  as="h1"
                  style="global/title-small"
                  testID="create-apply-link-title"
                >
                  <Trans>Apply link</Trans>
                </Text>
                <CreateApplyLinkForm
                  groups={groups}
                  isGroupsLoading={isGroupsLoading}
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  validateJsonForm={validateJsonForm}
                />
              </Container>
            </Layout>
          );
        }}
      </Formik>
      <BillingDetailsModal
        billingProfileId={billingProfileId}
        isOpen={isBillingDetailsModalOpen}
        onClose={() => setIsBillingDetailsModalOpen(false)}
      />
    </>
  );
}
